/* Hardtuned product interactions. No merchant copy or product-name matching here. */
(() => {
  'use strict';
  const observers = new Map();
  const updateClearance = () => document.body.classList.toggle('ht-purchase-controls-visible', [...observers.values()].some(item => item.visible));
  const observePurchaseControls = () => {
    for (const [element, item] of observers) { if (!element.isConnected) { item.observer.disconnect(); observers.delete(element); } }
    if (document.body.classList.contains('ht-mobile-chat-clearance')) {
      document.querySelectorAll('.main-product .product-form__buttons').forEach(element => {
        if (observers.has(element)) return;
        const item = {visible: false};
        item.observer = new IntersectionObserver(entries => { item.visible = entries.some(entry => entry.isIntersecting); updateClearance(); }, {rootMargin: '90px 0px 90px 0px'});
        observers.set(element, item); item.observer.observe(element);
      });
    }
    updateClearance();
  };
  observePurchaseControls();
  document.addEventListener('shopify:section:load', observePurchaseControls);
  document.addEventListener('shopify:section:unload', () => requestAnimationFrame(observePurchaseControls));
  document.addEventListener('click', event => {
    const button = event.target.closest?.('#Size-Guide-menu-drawer .ht-toggle button');
    if (!button || !['cmBtn', 'inchBtn'].includes(button.id)) return;
    const chart = button.closest('.ht-size-chart');
    if (!chart) return;
    event.preventDefault();
    const unit = button.id === 'inchBtn' ? 'inch' : 'cm';
    chart.querySelectorAll('[data-cm][data-inch]').forEach(cell => { cell.textContent = cell.dataset[unit]; });
    chart.querySelectorAll('.ht-toggle button').forEach(control => { const selected = control === button; control.classList.toggle('active', selected); control.setAttribute('aria-pressed', String(selected)); });
  });
})();

/* Monster Cart can restore an inherited overflow:hidden value on close.
 * Reconcile only a confirmed closed cart; never unlock an active theme drawer/modal.
 * This integration observes app lifecycle DOM because Monster owns its drawer code. */
(() => {
  'use strict';
  let cart = null, cartObserver = null, queued = false;
  const anotherOverlayOpen = () => {
    if (document.querySelector('details.menu-opening, cart-drawer.is-visible, quick-cart-drawer.is--open, dialog[open]')) return true;
    return [...document.querySelectorAll('[aria-modal="true"]')].some(element => {
      if (element === cart || cart?.contains(element)) return false;
      const style = getComputedStyle(element);
      return style.display !== 'none' && style.visibility !== 'hidden' && style.opacity !== '0' && element.getBoundingClientRect().height > 0;
    });
  };
  const reconcile = () => {
    queued = false;
    if (!cart?.isConnected || !cart.classList.contains('mu_closed') || cart.classList.contains('mu_openned') || anotherOverlayOpen()) return;
    // Remove only the stranded overflow value. Preserve position, scroll offset and other app styles.
    if (document.body.style.overflow === 'hidden') document.body.style.removeProperty('overflow');
  };
  const schedule = () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(reconcile);
  };
  const connect = () => {
    const nextCart = document.getElementById('monster-cart-wrapper');
    if (nextCart === cart) return;
    cartObserver?.disconnect();
    cart = nextCart;
    if (!cart) return;
    cartObserver = new MutationObserver(schedule);
    cartObserver.observe(cart, {attributes: true, attributeFilter: ['class']});
    schedule();
  };
  new MutationObserver(connect).observe(document.body, {childList: true, subtree: true});
  new MutationObserver(schedule).observe(document.body, {attributes: true, attributeFilter: ['style']});
  document.addEventListener('transitionend', schedule);
  window.addEventListener('pageshow', schedule);
  connect();
})();

/* Mobile product sticky CTA. The native setting controls this across all templates.
 * Show only before the real purchase button reaches the viewport, never below it.
 * Use the existing drawer API; no duplicate cart submission or variant logic. */
(() => {
  'use strict';
  if (!document.querySelector('.ht-mobile-product-cart')) return;
  const mobile = matchMedia('(max-width: 749px)');
  let frame = 0, opener = null;
  const update = () => {
    frame = 0;
    const bar = document.querySelector('.ht-mobile-product-cart');
    if (!bar) return;
    const target = document.querySelector('.main-product .product-form__submit');
    const viewportBottom = window.visualViewport ? visualViewport.offsetTop + visualViewport.height : innerHeight;
    const overlay = document.querySelector('quick-cart-drawer.is--open, #monster-cart-wrapper.mu_openned, details.menu-opening, cart-drawer.is-visible, dialog[open]');
    const show = mobile.matches && target && target.getClientRects().length && target.getBoundingClientRect().top >= viewportBottom && !overlay;
    bar.hidden = !show;
  };
  const schedule = () => { if (!frame) frame = requestAnimationFrame(update); };
  window.addEventListener('scroll', schedule, {passive: true});
  window.addEventListener('resize', schedule, {passive: true});
  window.addEventListener('pageshow', schedule);
  window.visualViewport?.addEventListener('resize', schedule, {passive: true});
  window.visualViewport?.addEventListener('scroll', schedule, {passive: true});
  mobile.addEventListener('change', schedule);
  document.addEventListener('shopify:section:load', schedule);
  document.addEventListener('load', schedule, true);
  // Drawers and app overlays change classes independently of page scrolling.
  new MutationObserver(schedule).observe(document.body, {subtree: true, attributes: true, attributeFilter: ['class'], childList: true});
  const product = document.querySelector('.main-product');
  if (product) new ResizeObserver(schedule).observe(product);
  document.fonts?.ready.then(schedule);
  document.addEventListener('click', async event => {
    const button = event.target.closest('[data-ht-product-quick-add]');
    if (!button || !mobile.matches || button.disabled || button.classList.contains('is--loading')) return;
    const drawer = document.querySelector('quick-cart-drawer');
    if (!drawer?.fetchProductForQuickCartDrawer) {
      document.querySelector('.main-product .product-form__buttons')?.scrollIntoView({block: 'center', behavior: 'smooth'});
      return;
    }
    opener = button;
    button.setAttribute('aria-busy', 'true');
    try { await drawer.fetchProductForQuickCartDrawer(event); }
    finally { button.removeAttribute('aria-busy'); }
  });
  document.addEventListener('opened', event => {
    if (event.target.matches('quick-cart-drawer') && opener) opener.setAttribute('aria-expanded', 'true');
    schedule();
  });
  document.addEventListener('closed', event => {
    if (!event.target.matches('quick-cart-drawer')) return;
    if (opener) {
      opener.setAttribute('aria-expanded', 'false');
      update();
      if (!opener.closest('.ht-mobile-product-cart').hidden) opener.focus({preventScroll: true});
      opener = null;
    }
    schedule();
  });
  update();
})();

/* Explicit product options. Validate before theme/app cart handlers; labels come
 * from the native option names, not product titles or hardcoded size-only rules. */
(() => {
  const requireOptions = event => {
    const info = event.target.closest?.('.main-product product-info');
    if (!info) return;
    if (event.type === 'click' && !event.target.closest('[name="add"], .shopify-payment-button')) return;
    if (event.type === 'submit' && !event.target.matches('form[data-type="add-to-cart-form"]')) return;
    const picker = info.querySelector('product-variant-selects');
    const missing = [...(picker?.querySelectorAll('[data-ht-option-field]') || [])].find(group => {
      const select = group.querySelector('select');
      return select ? !select.value : !group.querySelector('input:checked');
    });
    if (!missing) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const notice = picker.querySelector('.ht-size-prompt');
    if (notice) {
      const label = missing.dataset.htOptionName;
      notice.textContent = /^(size|colou?r|style)$/i.test(label) ? 'Please choose your ' + label.toLowerCase() : 'Please choose an option for “' + label + '”';
      notice.hidden = false;
    }
    missing.scrollIntoView({behavior: 'smooth', block: 'center'});
  };
  window.addEventListener('click', requireOptions, true);
  window.addEventListener('submit', requireOptions, true);
  document.addEventListener('change', event => {
    const picker = event.target.closest?.('.main-product product-variant-selects');
    const notice = picker?.querySelector('.ht-size-prompt');
    if (notice) notice.hidden = true;
  });
})();
